import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import api from '../utils/api';
import { Keyword } from '../types';

const MailSettings: React.FC = () => {
  const [enabled, setEnabled] = useState(false);
  const [sendTime, setSendTime] = useState('09:00');
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [recipients, setRecipients] = useState('');
  const [mailSubject, setMailSubject] = useState('');
  const [mailBody, setMailBody] = useState('');
  const [saved, setSaved] = useState(false);
  const [testSent, setTestSent] = useState(false);

  const { data: settingsData } = useQuery({
    queryKey: ['mail-settings'],
    queryFn: async () => {
      const res = await api.get('/mail-settings');
      return res.data;
    },
  });

  const { data: keywordsData } = useQuery({
    queryKey: ['keywords'],
    queryFn: async () => {
      const res = await api.get('/keywords');
      return res.data;
    },
  });

  useEffect(() => {
    if (settingsData) {
      setEnabled(settingsData.enabled || false);
      setSendTime(settingsData.send_time || '09:00');
      setSelectedKeywords(settingsData.keywords || []);
      setRecipients((settingsData.recipients || []).join('\n'));
      setMailSubject(settingsData.mail_subject || '');
      setMailBody(settingsData.mail_body || '');
    }
  }, [settingsData]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      await api.post('/mail-settings', {
        enabled,
        send_time:    sendTime,
        keywords:     selectedKeywords,
        recipients:   recipients.split('\n').map(r => r.trim()).filter(Boolean),
        mail_subject: mailSubject,
        mail_body:    mailBody,
      });
    },
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      await api.post('/mail-settings/test', {});
    },
    onSuccess: () => {
      setTestSent(true);
      setTimeout(() => setTestSent(false), 3000);
    },
  });

  const keywords: Keyword[] = keywordsData?.keywords?.filter((k: Keyword) => k.enabled) || [];

  const toggleKeyword = (kw: string) => {
    setSelectedKeywords(prev =>
      prev.includes(kw) ? prev.filter(k => k !== kw) : [...prev, kw]
    );
  };

  return (
    <div>
      <h1 className="page-title">メール設定</h1>

      <div className="card">
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20 }}>自動メール配信設定</h2>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={e => setEnabled(e.target.checked)}
              style={{ width: 18, height: 18 }}
            />
            <span style={{ fontSize: 15 }}>自動メール配信を有効にする</span>
          </label>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, color: '#94a3b8', marginBottom: 8 }}>
            送信時刻
          </label>
          <input
            type="time"
            className="input"
            value={sendTime}
            onChange={e => setSendTime(e.target.value)}
            style={{ maxWidth: 150 }}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, color: '#94a3b8', marginBottom: 8 }}>
            送付先メールアドレス（1行に1件）
          </label>
          <textarea
            className="input"
            value={recipients}
            onChange={e => setRecipients(e.target.value)}
            rows={4}
            placeholder="example@company.com&#10;sales@company.com"
            style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: 13 }}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, color: '#94a3b8', marginBottom: 8 }}>
            件名
            <span style={{ marginLeft: 8, color: '#475569', fontSize: 11 }}>
              {'{date}'} {'{count}'} {'{keywords}'} が使えます
            </span>
          </label>
          <input
            className="input"
            value={mailSubject}
            onChange={e => setMailSubject(e.target.value)}
            placeholder="【AdRadar】広告主リスト {date} ({count}件)"
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, color: '#94a3b8', marginBottom: 8 }}>
            本文
            <span style={{ marginLeft: 8, color: '#475569', fontSize: 11 }}>
              {'{date}'} {'{count}'} {'{keywords}'} が使えます
            </span>
          </label>
          <textarea
            className="input"
            value={mailBody}
            onChange={e => setMailBody(e.target.value)}
            rows={8}
            style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: 13 }}
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 13, color: '#94a3b8', marginBottom: 8 }}>
            対象キーワード（空の場合は全て）
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {keywords.map(kw => (
              <button
                key={kw.keyword_id}
                className={`btn ${selectedKeywords.includes(kw.keyword) ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => toggleKeyword(kw.keyword)}
              >
                {kw.keyword}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button
            className="btn btn-primary"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? '保存中...' : '💾 設定を保存'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => testMutation.mutate()}
            disabled={testMutation.isPending}
          >
            {testMutation.isPending ? '送信中...' : '📧 テスト送信'}
          </button>
          {saved && <span style={{ color: '#48bb78', fontSize: 13 }}>✅ 保存しました</span>}
          {testSent && <span style={{ color: '#48bb78', fontSize: 13 }}>✅ テストメールを送信しました</span>}
          {testMutation.isError && <span style={{ color: '#fc8181', fontSize: 13 }}>❌ 送信失敗</span>}
        </div>
      </div>
    </div>
  );
};

export default MailSettings;
